/**
 * VolumeFormPreviewWrapper
 * -------------------------
 * A dedicated layout wrapper for the Volume Discount Bundle Editor V2.
 * This is intentionally NOT shared with other bundle types.
 *
 * It provides:
 *  - The page meta-bar (back, title, badge, actions)
 *  - The left-panel tabs (Content / Design / Advanced)
 *  - The right-panel live preview
 *  - The Shopify SaveBar bridge connection (via RHF isDirty)
 *  - An optional variantSwitcher slot for the A/B VariantTabs component
 *
 * It consumes form state from the nearest FormProvider (react-hook-form)
 * via useFormContext() – the caller must ensure a FormProvider wraps this.
 */

import {
    ActionList,
    Badge,
    Button,
    ButtonGroup,
    FormLayout,
    Icon,
    Popover,
    SkeletonBodyText,
    SkeletonDisplayText,
    SkeletonThumbnail,
    Tabs,
    TextField,
    Tooltip,
} from "@shopify/polaris";
import styles from "./VolumeFormPreviewWrapper.module.css";
import React, { Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getBundleRoute } from "../../../constants";
import {
    ArrowLeftIcon,
    DuplicateIcon,
    StatusActiveIcon,
    MenuHorizontalIcon,
    ViewIcon,
    VariantIcon,
} from "@shopify/polaris-icons";
import { getBoolean } from "../../../../utilities/functions";
import { endPoints, getData, postData } from "../../../../utilities/networkCalls";
import { SaveBar } from "@shopify/app-bridge-react";
import { VolumeConfigInterface, ProductObj } from "../../Types";
import RenderError from "../../../../components/RenderError/RenderError";
import { BundleTypes, isBundleExpired, isBundleScheduled } from "../../constants";
import SuggestFeature from "../../../../components/SuggestFeature/SuggestFeature";
import { AppsContext } from "../../BundlesHomePage";
import LimitReachedPopup from "../../../../components/Plans/LimitReachedPopup";
import ActivateSnippetModal from "../../../../components/ActivateSnippetModal/ActivateSnippetModal";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import useIsMobile from "../../../../hooks/useIsMobile";
import CalloutNudgeBanner from "../../../../components/CalloutNudgeBanner";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import { useFormContext } from "react-hook-form";
import { VariantFieldContext } from "../../../../components/FormFields";
import { BASE_TARGET } from "../hooks/useBundleEditor";
import CustomTooltipAdmin from "../../../../components/CustomTooltipAdmin/CustomTooltipAdmin";

// ---------------------------------------------------------------------------
// Loading skeleton helpers
// ---------------------------------------------------------------------------

export const FormLoadingSkeleton = () => {
    return (
        <div style={{ paddingLeft: "13px" }}>
            {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{ marginTop: "10px" }}>
                    <div style={{ width: "55%", marginBottom: "7px" }}>
                        <SkeletonBodyText lines={1} />
                    </div>
                    <SkeletonDisplayText maxWidth="50%" />
                </div>
            ))}
        </div>
    );
};

export const PreviewLoadingSkeleton = () => {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                padding: "10px",
                borderRadius: "12px",
            }}
        >
            <div style={{ margin: "0 auto 10px auto" }}>
                <div style={{ width: "150px", textAlign: "center" }}>
                    <SkeletonDisplayText maxWidth="100%" />
                </div>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ width: "30px", marginRight: "10px" }}>
                    <SkeletonThumbnail size="small" />
                </div>
                <div style={{ width: "700px", marginBottom: "7px", marginRight: "80px" }}>
                    <SkeletonDisplayText maxWidth="100%" />
                </div>
                <SkeletonBodyText lines={2} />
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface VolumeFormPreviewWrapperProps {
    bundleConfig: VolumeConfigInterface;
    loading: boolean;
    savingBundle?: boolean;
    activatingBundle?: boolean;
    onSave: (param?: { src: string }) => Promise<void>;
    onDiscard: (param?: { src: string }) => void;
    onActivate: (currentStatus: boolean) => Promise<void>;
    onDuplicate: (name: string) => Promise<void>;
    previewableProducts?: ProductObj[];
    loadingPreviewableProducts?: boolean;
    currentActiveProduct?: string;
    setCurrentActiveProduct?: Dispatch<SetStateAction<string>>;
    forAiBundle?: boolean;
    selected: number;
    setSelected: (index: number) => void;
    renderForms: React.ReactNode[];
    renderPreview: React.ReactNode;
    /** Optional A/B VariantTabs component rendered above the form tabs */
    variantSwitcher?: React.ReactNode;
    handleNext: (index: number, name: string) => void;
    inspectorMode?: boolean;
    setInspectorMode?: (mode: boolean) => void;
    /** From useBundleEditor — stays true across variant switches until save/discard */
    hasUnsavedChanges?: boolean;
    /** Enables A/B testing (only used while it's not yet enabled) */
    onEnableAB?: () => void;
    /** Stops A/B testing (only used while it's enabled) */
    onStopAB?: () => void;
    /** True when the store's current plan doesn't include A/B testing */
    abTestingLocked?: boolean;
    lockedTooltipContent: string | ReactNode;
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const TABS = [
    { id: "Content-1", content: "Content", panelID: "Content-1" },
    { id: "Design-1", content: "Design", panelID: "Design-1" },
    { id: "Advanced-1", content: "Advanced", panelID: "Advanced-1" },
    // { id: "Settings-1", content: "Settings", panelID: "Settings-1" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const VolumeFormPreviewWrapper = ({
    bundleConfig,
    loading = false,
    savingBundle = false,
    activatingBundle = false,
    onSave,
    onDiscard,
    onActivate,
    onDuplicate,
    previewableProducts = [],
    currentActiveProduct,
    setCurrentActiveProduct,
    forAiBundle = false,
    selected,
    renderForms,
    renderPreview,
    variantSwitcher,
    handleNext,
    inspectorMode = false,
    setInspectorMode = () => {},
    hasUnsavedChanges,
    onEnableAB,
    onStopAB,
    abTestingLocked = false,
    lockedTooltipContent,
}: VolumeFormPreviewWrapperProps) => {
    // ------- context -------
    const goTo = useNavigate();
    const { setPublishCount } = useContext(AppsContext);
    const { plansInfo, globalSettingsInfo, appName } = useContext(AppConfigContext);
    const { isExhausted, isTrial, currentPlan, currentOrders } = plansInfo;
    const { isMobile } = useIsMobile();

    // ------- RHF (from parent FormProvider) -------
    const {
        formState: { isDirty, errors },
    } = useFormContext();

    // ------- A/B: which variant the admin currently has selected -------
    const bundleEditorContext = useContext(VariantFieldContext);
    const previewVariantId =
        bundleEditorContext?.abEnabled && bundleEditorContext.activeTarget !== BASE_TARGET
            ? bundleEditorContext.activeTarget
            : null;

    // ------- local state -------
    const [showLimitPopup, setShowLimitPopup] = useState(false);
    const [showEnableCoreSnippetModal, setShowEnableCoreSnippetModal] = useState(false);
    const [duplicateConfirmModal, setDuplicateConfirmModal] = useState(false);
    const [duplicateBundleLoading, setDuplicateBundleLoading] = useState(false);
    const [newBundleTitle, setNewBundleTitle] = useState("");
    const [duplicateErr] = useState("");
    const [showStoreModal, setShowStoreModal] = useState(false);
    const [previewLink, setPreviewLink] = useState("");
    const [popoverActive, setPopoverActive] = useState(false);
    const { bId } = useParams();

    // ------- Shopify SaveBar sync -------
    // Show save bar if the current form is dirty OR if any variant was previously
    // dirtied (hasUnsavedChanges stays true across variant switches, and also
    // covers changes — like adding/removing a variant — that never touch RHF's
    // own dirty state). The back button below must use this same combined
    // condition, otherwise it can navigate away "clean" while the save bar is
    // still armed (e.g. right after enableAB(), which resets RHF's isDirty but
    // sets hasUnsavedChanges) — leaving the save bar stuck shown on the next page.
    const hasChanges = isDirty || (hasUnsavedChanges ?? false);
    useEffect(() => {
        if (hasChanges) {
            shopify.saveBar.show("bundle-save-bar");
        } else {
            shopify.saveBar.hide("bundle-save-bar");
        }
    }, [hasChanges]);

    // ------- Publish count sync -------
    useEffect(() => {
        if (!loading) {
            setPublishCount((bundleConfig as any).publish_count || 0);
        }
    }, [loading, bundleConfig, setPublishCount]);

    // ------- Plan redirect -------
    useEffect(() => {
        if (!currentPlan || Object.keys(currentPlan).length === 0) {
            goTo("/billing");
        }
    }, [currentPlan, goTo]);

    // ------- Tab change -------
    const handleTabChange = useCallback(
        (selectedTabIndex: number) => {
            handleNext(selectedTabIndex, TABS[selectedTabIndex]?.content);
        },
        [handleNext],
    );

    // ------- Inspector toggle -------
    const handleInspectorToggle = () => {
        if (loading) return;
        setInspectorMode(!inspectorMode);
    };

    // ------- Preview URL helpers -------
    const buildPreviewUrl = () => {
        const shopBase = `https://${shopify.config.shop}`;
        if (bundleConfig?.volume_product_selection === "collection") {
            if (bundleConfig?.collections?.length > 0) {
                const collectionName = bundleConfig.collections[0].title.replace(/\s+/g, "-").toLowerCase();
                if (collectionName) return `${shopBase}/collections/${collectionName}`;
            }
            const handler = window.localStorage.getItem("qb_handle");
            return handler ? `${shopBase}/products/${handler}` : "";
        }
        if (bundleConfig?.volume_product_selection !== "specific") {
            const handler = window.localStorage.getItem("qb_handle");
            return handler ? `${shopBase}/products/${handler}` : "";
        }
        const handler = bundleConfig.products[0]?.handle || "";
        return handler ? `${shopBase}/products/${handler}` : "";
    };

    // Appends the currently selected A/B variant so "Show on Store"/"Preview
    // on Store" opens showing whichever variant (A, B, ...) the admin has
    // active in the editor, instead of a random/sticky visitor assignment.
    const withPreviewVariantParam = (url: string) => {
        if (!url || !previewVariantId) return url;
        try {
            const withParam = new URL(url);
            withParam.searchParams.set("kefi_preview_variant", previewVariantId);
            return withParam.toString();
        } catch (error) {
            return url;
        }
    };

    const handlePreview = () => {
        const url = withPreviewVariantParam(buildPreviewUrl());
        // Track first-time store preview modal
        const settingsObj = globalSettingsInfo?.globalSettings?.["bundle_settings_volume"];
        if (settingsObj?.show_on_store_modal_count === 0) {
            if (url) setPreviewLink(url);
            setShowStoreModal(true);
            postData({
                url: endPoints.updateBundleSettings,
                payload: { bundle_type: "volume", bundles_settings: { show_on_store_modal_count: 1 } },
            });
            settingsObj.show_on_store_modal_count = 1;
        }
        window.open(url, "_blank");
    };

    const handleDraftPreview = async () => {
        const isExtensionEnabled = await getData({ url: endPoints.extensionStatus });
        if (isExtensionEnabled.data.status === 0) {
            shopify.toast.show("Please enable the Kefi theme extension first.", { isError: true });
            return;
        }
        const fId = bundleConfig?.form_id;
        const shopBase = `https://${shopify.config.shop}`;
        const handler =
            bundleConfig?.volume_product_selection !== "specific"
                ? window.localStorage.getItem("qb_handle")
                : bundleConfig.products[0]?.handle;
        window.open(withPreviewVariantParam(`${shopBase}/products/${handler}?form_id=${fId}`), "_blank");
    };

    const handleActivateClick = async (currentStatus: boolean) => {
        if (!isTrial && isExhausted && !currentStatus) {
            setShowLimitPopup(true);
            return;
        }
        if (!currentStatus) {
            const isExtensionEnabled = await getData({ url: endPoints.extensionStatus });
            if (isExtensionEnabled.data.status === 0) {
                setShowEnableCoreSnippetModal(true);
                return;
            }
        }
        onActivate(currentStatus);
    };

    // ------- Duplicate -------
    const handleDuplicate = async () => {
        if (!newBundleTitle) return;
        setDuplicateBundleLoading(true);
        try {
            await onDuplicate(newBundleTitle);
            setDuplicateConfirmModal(false);
            setNewBundleTitle("");
        } finally {
            setDuplicateBundleLoading(false);
        }
    };

    // ------- Tooltip for publish -------
    const tooltipContent: React.ReactNode = isDirty ? (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img src="/svgs/upArrow.png" alt="Warning" style={{ width: 25, height: 25, transform: "rotate(-20deg)" }} />
            Please save the offer first!
        </span>
    ) : null;

    // ------- Derived error list for RenderError -------
    const flattenErrors = (errObj: any, path: string[] = []): { name: string; message: string }[] => {
        if (!errObj || typeof errObj !== "object") return [];
        if (typeof errObj.message === "string" && errObj.message.length) {
            if (appName === "bundles-local") {
                const name = path.join(".");
                return [{ name, message: name ? `${name}: ${errObj.message}` : errObj.message }];
            }
            return [{ name: path.join("."), message: errObj.message }];
        }
        const results: { name: string; message: string }[] = [];
        Object.keys(errObj).forEach((key) => {
            if (key === "type" || key === "ref" || key === "types") return;
            const val = errObj[key];
            if (val && typeof val === "object") {
                results.push(...flattenErrors(val, [...path, key]));
            }
        });
        return results;
    };
    const globalErrorsArray = flattenErrors(errors);

    // suppress unused warning
    void showStoreModal;
    void previewLink;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <div className={styles.formOuterWrapper} id="formPreviewWrapper">
            <div className={styles.formPreviewWrapper} key="volume">
                {/* ---- Sticky meta-bar ---- */}
                <div
                    style={{
                        backgroundColor: forAiBundle ? "transparent" : "#f1f1f1",
                        position: "sticky",
                        top: "0",
                        zIndex: "110",
                        paddingBottom: forAiBundle ? 0 : "16px",
                    }}
                >
                    {!forAiBundle && (
                        <div className={styles.metaBarWrapper}>
                            {/* Left side: back + title + badge */}
                            <div className={styles.metaBarWrapperLeft}>
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!hasChanges) {
                                            goTo(
                                                bundleConfig?.form_id?.length
                                                    ? getBundleRoute(BundleTypes.OFFERS)
                                                    : getBundleRoute("/"),
                                            );
                                        } else {
                                            shopify.saveBar.leaveConfirmation();
                                        }
                                    }}
                                    className={styles.backBtn}
                                >
                                    <ArrowLeftIcon width={18} />
                                </div>
                                <span className={styles.bundleName} style={{ fontSize: isMobile ? "1rem" : "" }}>
                                    {bId ? "Edit Volume Discount" : "Create Volume Discount"}
                                </span>
                                {!!bundleConfig?.form_id?.length && (
                                    <>
                                        {getBoolean(bundleConfig?.status) ? (
                                            isBundleScheduled(bundleConfig.start_date) ? (
                                                <Badge tone="attention">Scheduled</Badge>
                                            ) : isBundleExpired(bundleConfig.end_date) ? (
                                                <Badge>Expired</Badge>
                                            ) : bundleConfig?.is_paused ? (
                                                <Tooltip content="This offer is currently paused because you've reached your plan limit.">
                                                    <Badge tone="warning">Paused</Badge>
                                                </Tooltip>
                                            ) : (
                                                <Badge tone="success">Active</Badge>
                                            )
                                        ) : (
                                            <Badge tone="info">Draft</Badge>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Right side: action buttons */}
                            <div className={styles.metaBarWrapperRight}>
                                {getBoolean(bundleConfig.status) && (
                                    <span className={`${styles.secondaryBtn} ${styles.duplicateButton}`}>
                                        <Button
                                            onClick={handlePreview}
                                            disabled={
                                                !bundleConfig.status ||
                                                !bundleConfig?.form_id?.length ||
                                                loading ||
                                                isDirty
                                            }
                                        >
                                            Show on Store
                                        </Button>
                                    </span>
                                )}
                                {!getBoolean(bundleConfig.status) &&
                                    !bundleConfig?.product_bundle_id &&
                                    !!bundleConfig?.form_id && (
                                        <span className={`${styles.secondaryBtn} ${styles.duplicateButton}`}>
                                            <Button
                                                onClick={handleDraftPreview}
                                                disabled={!bundleConfig?.form_id || loading || isDirty}
                                            >
                                                Preview on Store
                                            </Button>
                                        </span>
                                    )}
                                <span className={`${styles.secondaryBtn} ${styles.duplicateButton}`}>
                                    <Button
                                        onClick={() => setDuplicateConfirmModal(true)}
                                        disabled={!bundleConfig?.form_id?.length || loading || isDirty}
                                    >
                                        Duplicate
                                    </Button>
                                </span>

                                <CustomTooltipAdmin content={lockedTooltipContent}>
                                    <span className={`${styles.secondaryBtn} ${styles.duplicateButton}`}>
                                        {bundleEditorContext?.abEnabled ? (
                                            <Button
                                                onClick={onStopAB}
                                                disabled={
                                                    abTestingLocked ||
                                                    loading ||
                                                    (isDirty && !!bundleConfig?.form_id?.length)
                                                }
                                            >
                                                Stop A/B Testing
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={onEnableAB}
                                                disabled={abTestingLocked || loading || savingBundle}
                                            >
                                                Enable A/B Testing
                                            </Button>
                                        )}
                                    </span>
                                </CustomTooltipAdmin>

                                {tooltipContent ? (
                                    <Tooltip content={tooltipContent} preferredPosition="above">
                                        <span style={{ display: "inline-block" }}>
                                            <Button
                                                variant="primary"
                                                onClick={() => handleActivateClick(!!getBoolean(bundleConfig.status))}
                                                loading={activatingBundle}
                                                disabled={
                                                    !bundleConfig?.form_id?.length ||
                                                    activatingBundle ||
                                                    loading ||
                                                    isDirty
                                                }
                                            >
                                                {getBoolean(bundleConfig.status) ? "Unpublish" : "Publish"}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        variant="primary"
                                        onClick={() => handleActivateClick(!!getBoolean(bundleConfig.status))}
                                        loading={activatingBundle}
                                        disabled={
                                            !bundleConfig?.form_id?.length || activatingBundle || loading || isDirty
                                        }
                                    >
                                        {getBoolean(bundleConfig.status) ? "Unpublish" : "Publish"}
                                    </Button>
                                )}

                                <div className={styles.moreActionsIcon}>
                                    <Popover
                                        active={popoverActive}
                                        activator={
                                            <div onClick={() => setPopoverActive((v) => !v)}>
                                                <Icon source={MenuHorizontalIcon} tone="base" />
                                            </div>
                                        }
                                        autofocusTarget="first-node"
                                        onClose={() => setPopoverActive(false)}
                                    >
                                        <ActionList
                                            actionRole="menuitem"
                                            items={[
                                                {
                                                    prefix: <Icon source={DuplicateIcon} tone="base" />,
                                                    content: "Duplicate",
                                                    onAction: () => setDuplicateConfirmModal(true),
                                                    disabled: !bundleConfig?.form_id?.length || loading || isDirty,
                                                },
                                                {
                                                    prefix: <Icon source={VariantIcon} tone="base" />,
                                                    content: bundleEditorContext?.abEnabled
                                                        ? "Stop A/B Testing"
                                                        : "Enable A/B Testing",
                                                    onAction: bundleEditorContext?.abEnabled ? onStopAB : onEnableAB,
                                                    disabled:
                                                        abTestingLocked ||
                                                        loading ||
                                                        (bundleEditorContext?.abEnabled
                                                            ? isDirty && !!bundleConfig?.form_id?.length
                                                            : savingBundle),
                                                },
                                                {
                                                    prefix: <Icon source={StatusActiveIcon} tone="base" />,
                                                    content: getBoolean(bundleConfig.status)
                                                        ? "Deactivate"
                                                        : "Activate",
                                                    disabled:
                                                        !bundleConfig?.form_id?.length ||
                                                        activatingBundle ||
                                                        loading ||
                                                        isDirty,
                                                    onAction: () =>
                                                        handleActivateClick(!!getBoolean(bundleConfig.status)),
                                                },
                                                {
                                                    prefix: <Icon source={ViewIcon} tone="base" />,
                                                    content: "Show on Store",
                                                    disabled:
                                                        !bundleConfig.status ||
                                                        !bundleConfig?.form_id?.length ||
                                                        loading ||
                                                        isDirty,
                                                    onAction: handlePreview,
                                                },
                                            ]}
                                        />
                                    </Popover>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ---- Validation errors ---- */}
                <RenderError submitErrors={globalErrorsArray} />

                {/* ---- Main panels ---- */}
                <div
                    className={styles.panels}
                    style={loading ? {} : forAiBundle ? { padding: "1rem", background: "#f1f1f1" } : {}}
                >
                    {/* Left: A/B switcher + content tabs */}
                    <div className={styles.leftSection}>
                        {variantSwitcher}
                        <Tabs tabs={TABS} selected={selected} onSelect={handleTabChange} disabled={loading}>
                            <div
                                className={styles.selectedTabPanel}
                                style={{ pointerEvents: loading ? "none" : "all" }}
                            >
                                {loading ? <FormLoadingSkeleton /> : renderForms[selected]}
                            </div>
                        </Tabs>
                    </div>

                    {/* Right: live preview */}
                    <div className={styles.rightSection} style={{ top: forAiBundle ? 69 : 64 }}>
                        <div className={styles.preview}>
                            {!loading && (
                                <div
                                    className={styles.previewWidgetTitle}
                                    style={{ color: loading ? "#b5b5b5" : "#4a4a4a" }}
                                >
                                    <div style={{ zoom: 0.9 }}>
                                        <ButtonGroup variant="segmented">
                                            <Button
                                                pressed={!inspectorMode}
                                                onClick={() => {
                                                    if (inspectorMode) handleInspectorToggle();
                                                }}
                                                disabled={savingBundle || activatingBundle || loading}
                                            >
                                                Preview
                                            </Button>
                                            <Button
                                                pressed={inspectorMode}
                                                onClick={() => {
                                                    if (!inspectorMode) handleInspectorToggle();
                                                }}
                                                disabled={savingBundle || activatingBundle || loading}
                                            >
                                                Design Editor
                                            </Button>
                                        </ButtonGroup>
                                    </div>
                                </div>
                            )}

                            {loading ? (
                                <PreviewLoadingSkeleton />
                            ) : (
                                <>
                                    {(bundleConfig?.volume_product_selection === "specific"
                                        ? bundleConfig.products.length > 1
                                        : previewableProducts.length > 1) && (
                                        <div
                                            style={{
                                                marginBottom: "10px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px",
                                            }}
                                        >
                                            <span style={{ fontSize: "12px", color: "#4a4a4a" }}>Product: </span>
                                            <select
                                                className={styles.select}
                                                name="widget_product_selector"
                                                onChange={(e) =>
                                                    setCurrentActiveProduct && setCurrentActiveProduct(e.target.value)
                                                }
                                                value={currentActiveProduct}
                                            >
                                                {bundleConfig?.volume_product_selection === "specific"
                                                    ? bundleConfig.products.map((sp) => (
                                                          <option key={sp.id} value={sp.id}>
                                                              {sp.title}
                                                          </option>
                                                      ))
                                                    : previewableProducts.map((sp) => (
                                                          <option key={sp.id} value={sp.id}>
                                                              {sp.title}
                                                          </option>
                                                      ))}
                                            </select>
                                        </div>
                                    )}
                                    {renderPreview}
                                </>
                            )}
                        </div>
                        {!loading && <CalloutNudgeBanner />}
                        {!loading && <SuggestFeature bundleType="volume" />}
                    </div>
                </div>

                {/* ---- Duplicate modal ---- */}
                {duplicateConfirmModal && (
                    <CustomModal
                        title="Duplicate Bundle"
                        open={duplicateConfirmModal}
                        loading={duplicateBundleLoading}
                        onClose={() => setDuplicateConfirmModal(false)}
                        primaryAction={{
                            content: "Duplicate",
                            disabled: !newBundleTitle || !!duplicateErr.length || duplicateBundleLoading,
                            onAction: handleDuplicate,
                        }}
                        secondaryActions={[
                            {
                                content: "Cancel",
                                onAction: () => setDuplicateConfirmModal(false),
                            },
                        ]}
                    >
                        <FormLayout>
                            <TextField
                                label="Title"
                                placeholder="bundle title"
                                autoComplete=""
                                value={newBundleTitle}
                                onChange={(val) => setNewBundleTitle(val)}
                                error={duplicateErr}
                            />
                        </FormLayout>
                    </CustomModal>
                )}

                {/* ---- Limit popup ---- */}
                {showLimitPopup && (
                    <LimitReachedPopup
                        setPopup={setShowLimitPopup}
                        isExhausted={isExhausted}
                        currentPlan={currentPlan}
                        currentOrders={currentOrders}
                    />
                )}

                {showEnableCoreSnippetModal && (
                    <ActivateSnippetModal
                        setShowEnableCoreSnippetModal={setShowEnableCoreSnippetModal}
                        onSnippetEnabled={() => onActivate(!!getBoolean(bundleConfig.status))}
                    />
                )}

                {/* ---- Shopify Save Bar (appears when form is dirty) ---- */}
                {savingBundle ? (
                    <SaveBar id="bundle-save-bar">
                        <button variant="primary" loading="" onClick={() => onSave({ src: "Top Bar" })} disabled>
                            Save
                        </button>
                        <button onClick={() => onDiscard({ src: "Top Bar" })} disabled>
                            Discard
                        </button>
                    </SaveBar>
                ) : (
                    <SaveBar id="bundle-save-bar">
                        <button variant="primary" onClick={() => onSave({ src: "Top Bar" })}>
                            Save
                        </button>
                        <button onClick={() => onDiscard({ src: "Top Bar" })}>Discard</button>
                    </SaveBar>
                )}
            </div>
        </div>
    );
};
